/**
 * Centralized Authorization Configuration for HRM System
 *
 * This file contains all authorization rules for different modules and features.
 * It provides a single source of truth for access control across the application.
 */

/**
 * @typedef {Object} AuthConfig
 * @property {string[]} [roles] - Required roles for access
 * @property {string} [module] - Required module for access
 * @property {string} [feature] - Required feature for access
 * @property {boolean} [requireAll=false] - If true, user must have ALL specified roles/features
 * @property {string} [fallback="access-denied"] - Type of fallback ("access-denied" or "upgrade-plan")
 * @property {string} [description] - Description of what this authorization controls
 */

// ================================
// ESS (Employee Self Service) Module
// ================================
export const ESS_AUTH = {
  // Leave Management
  MY_LEAVE: {
    feature: "ESS_MY_LEAVE",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "View personal leave history",
  },
  APPLY_LEAVE: {
    feature: "ESS_APPLY_LEAVE",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "Apply for new leave",
  },
  FALLBACK: {
    feature: "ESS_FALLBACK",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "Fallback leave management",
  },

  // Attendance Management
  MY_LOGS: {
    feature: "ESS_MY_LOGS",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "View personal attendance logs",
  },
  MY_TIME_SHEET: {
    feature: "ESS_MY_TIME_SHEET",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "View and manage personal timesheet",
  },

  // Financial
  REIMBURSEMENTS: {
    feature: "ESS_REIMBURSEMENTS",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "Manage personal reimbursements",
  },
  EMERGENCY_FUNDS: {
    feature: "ESS_LOAN",
    fallback: "upgrade-plan", // Show upgrade if no access
    description: "Apply for emergency funds/loans",
  },

  // Payroll
  PAYROLL: {
    feature: "ESS_PAYROLL",
    fallback: "upgrade-plan",
    description: "View personal payroll details, payslips, and salary breakup",
  },

  // General (No authorization required)
  HOLIDAYS: {
    description: "View company holidays list",
    // No feature/roles = public access, no fallback needed
  },
  POLICY: {
    description: "View company policies",
    // No feature/roles = public access, no fallback needed
  },
};

// ================================
// TEAM Module (Manager Functions)
// ================================
export const TEAM_AUTH = {
  // Attendance Management
  MARK_ATTENDANCE: {
    roles: ["ROLE_MANAGER", "ROLE_ADMIN"],
    module: "ATTENDANCE",
    feature: "TEAM_MARK_ATTENDANCE",
    description: "Mark attendance for team members",
  },
  DASHBOARD: {
    roles: ["ROLE_MANAGER"],
    module: "ATTENDANCE",
    feature: "TEAM_LOGS",
    description: "View team attendance dashboard",
  },
  MANAGE_ATTENDANCE: {
    roles: ["ROLE_MANAGER"],
    module: "ATTENDANCE",
    feature: "TEAM_MANAGE_ATTENDANCE",
    description: "Manage team attendance requests",
  },
  TIME_SHEET: {
    roles: ["ROLE_MANAGER"],
    module: "ATTENDANCE",
    feature: "TEAM_TIME_SHEET",
    description: "Manage team timesheets",
  },
  FALLBACK: {
    roles: ["ROLE_MANAGER"],
    module: "ATTENDANCE",
    feature: "TEAM_FALLBACK",
    description: "Manage attendance fallback",
  },

  // Leave Management
  MANAGE_LEAVE: {
    roles: ["ROLE_MANAGER"],
    module: "LEAVE",
    feature: "TEAM_MANAGE_LEAVE",
    description: "Approve/reject team leave requests",
  },
  APPLY_LEAVE: {
    roles: ["ROLE_MANAGER"],
    module: "LEAVE",
    feature: "TEAM_LEAVE_APPLY",
    fallback: "upgrade-plan",
    description: "Apply leave on behalf of team members",
  },

  // HR Functions
  LOAN_APPROVAL: {
    roles: ["ROLE_HR"],
    module: "PAYROLL",
    feature: "TEAM_LOAN_APPROVAL",
    description: "Approve emergency fund requests",
  },
  PAYROLL: {
    roles: ["ROLE_HR"],
    module: "PAYROLL",
    feature: "TEAM_PAYROLL",
    description: "Manage payroll operations",
  },
  REIMBURSEMENTS: {
    roles: ["ROLE_MANAGER"],
    module: "PAYROLL",
    feature: "TEAM_REIMBURSEMENTS",
    description: "Approve team reimbursement requests",
  },
};

// ================================
// ADMIN Module
// ================================
export const ADMIN_AUTH = {
  // System Management
  MASTER: {
    roles: ["ROLE_ADMIN"],
    feature: "ADMIN_MASTER", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage master data for the application.",
  },
  USER_MANAGEMENT: {
    roles: ["ROLE_ADMIN"],
    feature: "ADMIN_USER_MANAGEMENT",
    fallback: "upgrade-plan",
    description: "Manage system users, roles, and permissions.",
  },
  INCIDENT_REPORT: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    feature: "ADMIN_INCIDENT_REPORT", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "View and manage incident reports.",
  },

  // Payroll Management (within Admin section)
  SALARY_MASTER: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_SALARY_MASTER", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage the master salary configurations.",
  },
  PAYMENT_SETUP: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_PAYMENT_SETUP", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Configure payment and bank details.",
  },
  REIMBURSEMENTS: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_REIMBURSEMENTS", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage reimbursement types and policies.",
  },
  ALLOWANCES: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_ALLOWANCES", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage different types of allowances.",
  },
  DEDUCTIONS: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_DEDUCTIONS", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage different types of deductions.",
  },
  INCOME_TAX: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_INCOME_TAX", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage income tax settings and slabs.",
  },
  PROFESSIONAL_TAX: {
    roles: ["ROLE_ADMIN", "ROLE_HR"],
    module: "PAYROLL",
    feature: "ADMIN_PROFESSIONAL_TAX", // Assuming a feature name
    fallback: "upgrade-plan",
    description: "Manage professional tax settings.",
  },
};

// ================================
// COMPENSATION module
// ================================

export const COMPENSATION_AUTH = {
  PAYROLL_REPORT: {
    roles: ["ROLE_ADMIN", "ROLE_HR", "ROLE_ACCOUNTANT"],
    feature: "COMP_MANAGE_PAYROLL_REPORT",
    fallback: "upgrade-plan",
    description:
      "Access payroll reports including salary register, budget, and summary.",
  },
  PAYROLL: {
    roles: ["ROLE_ADMIN", "ROLE_ACCOUNTANT"],
    feature: "COMP_MANAGE_PAYROLL",
    fallback: "upgrade-plan",
    description: "Manage and process company payroll.",
  },
};

export const EMAIL_AUTOMATION_AUTH = {
  VIEW: {
    feature: "EMAIL_AUTOMATION",
    fallback: "upgrade-plan",
    description: "Access and manage all email automation and notifications.",
  },
};

// ================================
// COMBINED CONFIGURATIONS
// ================================
export const ALL_AUTH_CONFIGS = {
  ESS: ESS_AUTH,
  TEAM: TEAM_AUTH,
  ADMIN: ADMIN_AUTH,
  COMPENSATION: COMPENSATION_AUTH,
  EMAIL_AUTOMATION: EMAIL_AUTOMATION_AUTH,
};

// ================================
// HELPER FUNCTIONS
// ================================

/**
 * Get authorization config for a specific module and feature
 * @param {string} module - Module name (e.g., 'ESS', 'TEAM')
 * @param {string} feature - Feature name within the module
 * @returns {AuthConfig|null} - Authorization configuration or null if not found
 */
export const getAuthConfig = (module, feature) => {
  const moduleConfig = ALL_AUTH_CONFIGS[module.toUpperCase()];
  if (!moduleConfig) return null;

  return moduleConfig[feature.toUpperCase()] || null;
};

/**
 * Get all features available for a specific role
 * @param {string} role - Role name (e.g., 'ROLE_MANAGER')
 * @returns {string[]} - Array of feature identifiers
 */
export const getFeaturesForRole = (role) => {
  const features = [];

  Object.values(ALL_AUTH_CONFIGS).forEach((moduleConfig) => {
    Object.entries(moduleConfig).forEach(([featureName, config]) => {
      if (config.roles && config.roles.includes(role)) {
        features.push(config.feature || featureName);
      }
    });
  });

  return features;
};

/**
 * Check if a feature requires premium/upgrade
 * @param {string} module - Module name
 * @param {string} feature - Feature name
 * @returns {boolean} - True if feature requires upgrade
 */
export const requiresUpgrade = (module, feature) => {
  const config = getAuthConfig(module, feature);
  return config?.fallback === "upgrade-plan";
};

/**
 * Generate menu items with authorization for any module
 * @param {Object[]} menuItems - Array of menu item configurations
 * @param {string} module - Module name for auth config lookup
 * @returns {Object[]} - Menu items with proper auth configuration
 */
export const generateAuthorizedMenuItems = (menuItems, module) => {
  return menuItems.map((item) => {
    if (item.authKey) {
      const authConfig = getAuthConfig(module, item.authKey);
      return {
        ...item,
        auth: authConfig,
      };
    }
    return item;
  });
};

// ================================
// USAGE EXAMPLES
// ================================

// Example: ESS Menu Configuration
export const ESS_MENU_ITEMS = [
  {
    name: "Leave",
    subItems: ["My Leave", "Apply Leave", "Fallback"],
    routes: ["/ess/my-leave", "/ess/apply-leave", "/ess/fallback"],
    subItemsAuth: [ESS_AUTH.MY_LEAVE, ESS_AUTH.APPLY_LEAVE, ESS_AUTH.FALLBACK],
  },
  {
    name: "Attendance",
    subItems: ["My Logs", "My Time Sheet"],
    routes: ["/ess/my-logs", "/ess/my-time-sheet"],
    subItemsAuth: [ESS_AUTH.MY_LOGS, ESS_AUTH.MY_TIME_SHEET],
  },
  {
    name: "Reimbursements",
    routes: ["/ess/reimbursements"],
    auth: ESS_AUTH.REIMBURSEMENTS,
  },
  {
    name: "Emergency Funds",
    routes: ["/ess/emergency-funds"],
    auth: ESS_AUTH.EMERGENCY_FUNDS,
  },
  {
    name: "Payroll", // ✅ NEW
    routes: ["/ess/payroll"],
    auth: ESS_AUTH.PAYROLL,
  },
  {
    name: "List Of Holidays",
    routes: ["/ess/list-of-holidays"],
    auth: ESS_AUTH.HOLIDAYS, // Public access
  },
  {
    name: "Policy",
    routes: ["/ess/Policy"],
    auth: ESS_AUTH.POLICY, // Public access
  },
];

// Example: Team Menu Configuration
export const TEAM_MENU_ITEMS = [
  {
    name: "Mark Attendance",
    routes: ["/team/mark-attendance"],
    auth: TEAM_AUTH.MARK_ATTENDANCE,
    notificationKey: "Mark Attendance",
  },
  {
    name: "Dashboard",
    routes: ["/team/team-logs"],
    auth: TEAM_AUTH.DASHBOARD,
    notificationKey: "Dashboard",
  },
  {
    name: "Manage Leave",
    routes: ["/team/manage-team-leave"],
    auth: TEAM_AUTH.MANAGE_LEAVE,
    notificationKey: "Manage Leave",
  },
  {
    name: "Manage Fallback",
    routes: ["/team/team-fallback"],
    auth: TEAM_AUTH.FALLBACK,
  },
  {
    name: "Apply Leave",
    routes: ["/team/team-leave-apply"],
    auth: TEAM_AUTH.APPLY_LEAVE,
  },
  {
    name: "Manage Attendance",
    routes: ["/team/manage-attendance"],
    auth: TEAM_AUTH.MANAGE_ATTENDANCE,
    notificationKey: "Manage Attendance",
  },
  {
    name: "Time Sheet",
    routes: ["/team/team-time-sheet"],
    auth: TEAM_AUTH.TIME_SHEET,
    notificationKey: "Team Time Sheet", // 🔔 add this
  },
  {
    name: "Loan Approval",
    routes: ["/team/team-loan-approval"],
    auth: TEAM_AUTH.LOAN_APPROVAL,
    notificationKey: "Loan Approval",
  },
  {
    name: "Payroll",
    routes: ["/team/comp-payroll"],
    auth: TEAM_AUTH.PAYROLL,
    notificationKey: "Payroll",
  },
  {
    name: "Reimbursements",
    routes: ["/team/team-reimbursements"],
    auth: TEAM_AUTH.REIMBURSEMENTS,
    notificationKey: "Reimbursements",
  },
];

// Example: Compensation & Benefits Menu Configuration
export const COMPENSATION_MENU_ITEMS = [
  {
    name: "Payroll Report",
    routes: [
      "/compensation-benefits/payroll-report/salary-register",
      "/compensation-benefits/payroll-report/salary-budget",
      "/compensation-benefits/payroll-report/summary",
    ],
    subItems: ["Salary Register", "Salary Budget", "Payroll Summary"],
    auth: COMPENSATION_AUTH.PAYROLL_REPORT,
  },
  {
    name: "Payroll",
    routes: ["/compensation-benefits/payroll"],
    auth: COMPENSATION_AUTH.PAYROLL,
  },
];

export const ADMIN_MENU_ITEMS = [
  // Note: The "Master" link is now implicitly handled by MASTER_PAGES_CONFIG inside the Master component.
  // We just need the top-level links here.
  {
    name: "Master",
    routes: ["/admin/master"],
    auth: { roles: ["ROLE_ADMIN", "ROLE_HR"] },
  }, // A general check to see the "Master" button
  {
    name: "User Management",
    routes: ["/admin/user-management"],
    auth: ADMIN_AUTH.USER_MANAGEMENT,
  },
  {
    name: "Payroll",
    subItems: [
      "Salary Master",
      "Payment Setup",
      "Reimbursements",
      "Allowances",
      "Deductions",
      "Income Tax",
      "Professional Tax",
    ],
    routes: [
      "/admin/salary-master",
      "/admin/payment-setup",
      "/admin/reimbursements",
      "/admin/allowances",
      "/admin/deductions",
      "/admin/income-tax",
      "/admin/professional-tax",
    ],
    auth: { roles: ["ROLE_ADMIN", "ROLE_HR"], module: "PAYROLL" },
  },
];
